import { CloseOutlined, SaveOutlined } from '@ant-design/icons'
import { Button, Typography } from 'antd'
import {
  EnrichedOntologyFieldDefinition,
  OntologyValueType,
} from 'features/dynamicOntology'
import { RolodexDocumentEntry } from 'features/files'
import { useLibraDocument } from 'features/libra/document/LibraDocumentContext'
import { FieldCategory } from 'features/libra/ontology/ontologyFieldTypes'
import { useTicketOntologyFields } from 'features/libra/ontology/useOntologyFields'
import { LeafActionsParams, RecursivePropertyView } from 'features/objectViews'
import React, {
  FC,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'

import {
  useDocumentFieldValues,
  useSaveDocumentFieldValues,
} from './documentFieldsApi'
import { InferenceButton } from './inference/InferenceButton'

const { Text } = Typography

interface DocumentFieldExtractorProps {
  selectedDocument: RolodexDocumentEntry | null
}

const Container = styled.div`
  background: ${({ theme }) => theme.forecast.background2};
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
`

const SaveButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
`

export const DocumentFieldExtractor: FC<DocumentFieldExtractorProps> = ({
  selectedDocument,
}) => {
  // Document extractor writes to ticket-scope only — no component tabs.
  const { ontologyFields } = useTicketOntologyFields()
  const { unitId } = useLibraDocument()

  // Get field values for the selected document
  const {
    fieldValues: savedFieldValues,
    entryId,
    forceRefresh,
  } = useDocumentFieldValues(selectedDocument)
  const { doSave } = useSaveDocumentFieldValues()

  // Local state for current field values and entry ID
  const [currentFieldValues, setCurrentFieldValues] =
    useState<Record<string, unknown>>(savedFieldValues)
  const [currentEntryId, setCurrentEntryId] = useState<string | undefined>(
    entryId
  )
  const [hasChanges, setHasChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Filter to only input category fields and transform to RecursivePropertyView format
  // RecursivePropertyView expects Record<string, [string, EnrichedOntologyFieldDefinition]>
  const inputOntologyFields = useMemo<
    Record<string, [string, EnrichedOntologyFieldDefinition]>
  >(() => {
    return Object.fromEntries(
      Object.entries(ontologyFields)
        .filter(([, field]) => field.category === FieldCategory.INPUT)
        .map(([key, field]) => [
          key,
          [
            key,
            {
              ...field,
              key,
              entryType: 'ontology-field',
              keyOrField: 'key',
            } as EnrichedOntologyFieldDefinition,
          ],
        ])
    )
  }, [ontologyFields])

  // Create virtual object with only input fields
  const virtualObject = useMemo<OntologyValueType>(() => {
    return Object.fromEntries(
      Object.keys(inputOntologyFields).map((key) => [
        key,
        currentFieldValues[key],
      ])
    ) as OntologyValueType
  }, [inputOntologyFields, currentFieldValues])

  const handleFieldChange = useCallback(
    (updates: SetStateAction<OntologyValueType | undefined>) => {
      setCurrentFieldValues((prev) => {
        const newValues =
          typeof updates === 'function'
            ? updates(prev as OntologyValueType | undefined)
            : updates
        return (newValues ?? {}) as Record<string, unknown>
      })
      setHasChanges(true)
    },
    []
  )

  const handleSave = async () => {
    if (!selectedDocument || !unitId) {
      console.error('[Field Extractor] Missing document or unitId')
      return
    }

    setIsSaving(true)
    try {
      const newEntryId = await doSave(
        selectedDocument,
        unitId,
        currentFieldValues,
        currentEntryId
      )

      // Set the entry ID BEFORE refreshing to prevent race condition
      setCurrentEntryId(newEntryId)
      setHasChanges(false)

      // Refresh to get the latest data from server
      await forceRefresh()
    } catch (error) {
      console.error('[Field Extractor] Error saving field values:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setCurrentFieldValues(savedFieldValues)
    setCurrentEntryId(entryId)
    setHasChanges(false)
  }

  const leafActions = useCallback(
    ({ fieldDef, setObject }: LeafActionsParams) => {
      return setObject && selectedDocument ? (
        <InferenceButton
          fieldDef={fieldDef}
          setValue={setObject}
          documentId={selectedDocument.entry_id}
        />
      ) : null
    },
    [selectedDocument]
  )

  return (
    <Container>
      <Text strong style={{ fontSize: 16, marginBottom: 16, display: 'block' }}>
        Extracted Fields
        {selectedDocument && (
          <Text type='secondary' style={{ fontSize: 14, marginLeft: 8 }}>
            ({selectedDocument.visible_name || selectedDocument.filename})
          </Text>
        )}
      </Text>

      <RecursivePropertyView
        label={undefined}
        object={virtualObject}
        setObject={handleFieldChange}
        ontologyFields={inputOntologyFields}
        leafActions={leafActions}
      />

      {hasChanges && (
        <SaveButtonContainer>
          <Button
            icon={<CloseOutlined />}
            onClick={handleCancel}
            disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type='primary'
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={isSaving}
            disabled={!selectedDocument}>
            Save to{' '}
            {selectedDocument?.visible_name ||
              selectedDocument?.filename ||
              'Document'}
          </Button>
        </SaveButtonContainer>
      )}
    </Container>
  )
}
